import React, { FunctionComponent, ReactNode } from "react";
import { HiOutlineCheckCircle, HiOutlineInformationCircle } from "react-icons/hi";
import TenantLogo from "../TenantLogo/TenantLogo";
import useTenantLogo from "../TenantLogo/useTenantLogo";

interface GeoCaptureCompletedProps {
  /** The tenant logo URL; "" omits the logo block. */
  logo: string;
  /** A returning learner who cannot submit again, rather than one who just did (the same two states `FormCompleted` has). */
  alreadySubmitted?: boolean;
  /** Rendered under the message, e.g. the auto-close note. */
  children?: ReactNode;
}

/**
 * The geo capture result and already-submitted screens: a compact, viewport-fitting screen (the shared
 * `FormCompleted` illustration is too large here) under the tenant logo, which sits where the capture form's
 * card has it (the same column, left-aligned), so the logo stays put when the result replaces the form.
 * The headline and message copy match `FormCompleted`.
 */
const GeoCaptureCompleted: FunctionComponent<GeoCaptureCompletedProps> = ({ logo, alreadySubmitted = false, children }) => {
  const Icon = alreadySubmitted ? HiOutlineInformationCircle : HiOutlineCheckCircle;
  return (
    <div className="flex min-h-screen flex-col">
      {logo && (
        <div className="px-4 pt-6">
          <div className="mx-auto flex w-full max-w-xl flex-col">
            <TenantLogo src={logo} />
          </div>
        </div>
      )}
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-8 text-center">
        <div
          className={`flex h-14 w-14 items-center justify-center rounded-full ${
            alreadySubmitted ? "bg-primary-1 text-primary-6" : "bg-success-2 text-success-1"
          }`}
        >
          <Icon className="h-8 w-8" aria-hidden="true" />
        </div>
        <h1 className="mt-4 text-xl font-semibold text-neutral-12">{alreadySubmitted ? "Already Submitted" : "Submitted Successfully!"}</h1>
        <p className="mt-2 max-w-sm text-sm text-neutral-9">
          {alreadySubmitted ? "You have already responded to this form. It accepts one response only." : "Thank you! Your response has been recorded."}
        </p>
        {children}
      </div>
    </div>
  );
};

/**
 * The "Already Submitted" screen BiFormLayout shows in front of the geo capture layout. It is routed before
 * `GeoCaptureForm` mounts, so it reads the tenant logo itself.
 */
export const GeoCaptureAlreadySubmitted: FunctionComponent = () => {
  const logo = useTenantLogo();
  return <GeoCaptureCompleted logo={logo} alreadySubmitted />;
};

export default GeoCaptureCompleted;
